import { EventDispatcher } from 'library/ts/events/EventDispatcher';
import { Country } from 'library/ts/model/enums/geo/Country';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { API } from 'tag/ts/tag/API';
import { TagController } from 'tag/ts/tag/controller/TagController';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventDataVO } from 'tag/ts/tag/events/TagEventDataVO';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { PerformanceMetricsManager } from 'tag/ts/tag/metrics/PerformanceMetricsManager';
import { DefaultTagConfig } from 'tag/ts/tag/model/config/DefaultTagConfig';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { TagModel } from 'tag/ts/tag/model/TagModel';
import { ConfigType } from 'tag/ts/tag/model/type/ConfigType';
import { ParamsVO } from 'tag/ts/tag/model/vo/ParamsVO';
import { platformSupported } from 'tag/ts/tag/utilities/PlatformSupport';
import { TagSnapshot } from 'tag/ts/tag/utilities/TagSnapshot';
import { TagUtilities } from 'tag/ts/tag/utilities/TagUtilities';
import { ErrorManager } from 'tag/ts/tag/view/ErrorManager';
import { TagView } from 'tag/ts/tag/view/TagView';
export class Tag {
    #utils = window?.SmartTag?.utilities;
    #runtimeVO = window?.SmartTag?.runtimeVO;
    #uuid;
    #eventDispatcher;
    #params;
    #callbacks;
    #model;
    #view;
    #controller;
    #api;
    #errorManager;
    #snapshot;
    #logger;
    #tagUtilities;
    #onReadyResolve;
    #onFailureReject;
    constructor(argument) {
        this.#onReadyResolve = argument?.resolve;
        this.#onFailureReject = argument?.reject;
        this.#uuid =
            argument?.paramsVO?.TAG?.uuid
                ||
                    this.#utils.String.playerServicesUUID();
        this.#logger = LogService.create({ prefixes: ['SmartTag:', 'Tag:', this.uuid, this.name] });
        this.#tagUtilities = new TagUtilities({ tag: this });
        this.#params = new ParamsVO();
        this.#callbacks = argument?.callbacksVO;
        this.#eventDispatcher = new EventDispatcher(this?.name);
        if (this.#utils.Type.isEmpty(argument?.paramsVO) === false && this.#utils.Type.isObject(argument?.paramsVO)) {
            argument.paramsVO = this.tagUtilities.normalizeParams(argument?.paramsVO);
            this.params = this.#utils.DeepMerge.deepmerge(this.params, argument?.paramsVO);
            this.params = this.tagUtilities.namespaceConverter(this.params);
            this.#logger.log('params [from the page]:', this.params);
            this.params.TAG.container = this.#utils.DOM.getElementAdvanced(this.params.TAG.container);
            if (this.#utils.Type.isElement(this?.params?.TAG?.container)) {
                this.#utils.Delay.delay().then(() => this.#initialize());
            }
            else {
                this.#logger.warn('Constructor:', 'Tag container was not found:', argument.paramsVO);
                this.#onFailureReject({ msg: 'Error: Tag container was not found! Abort!', tag: this });
            }
        }
        else {
            this.#logger.warn('Constructor:', 'paramsVO:', argument.paramsVO);
        }
    }
    get name() {
        return 'Tag';
    }
    get uuid() {
        return this.#uuid;
    }
    get params() {
        return this.#params;
    }
    set params(value) {
        this.#params = value;
    }
    get callbacks() {
        return this.#callbacks;
    }
    set callbacks(value) {
        this.#callbacks = value;
    }
    get tagUtilities() {
        return this.#tagUtilities;
    }
    get country() {
        return this?.params?.CONTENT?.country || this.#runtimeVO?.top?.country || Country.US;
    }
    get performanceMetricsManager() {
        return PerformanceMetricsManager.getInstance();
    }
    get eventDispatcher() {
        return this.#eventDispatcher;
    }
    getTagEventDataVO = () => {
        return new TagEventDataVO();
    };
    getTagEventVO = (type = undefined, tag = undefined, data = undefined, target = undefined, targetName = undefined) => {
        return new TagEventVO(type, tag, data, target, targetName || target?.name);
    };
    tagEventsHandler = (callback, events, subscriber = undefined) => {
        if (this.#utils.Type.isDefined(callback) && this.#utils.Type.isDefined(events)) {
            if (this.#utils.Type.isArray(events)) {
                for (let i = 0; i < events.length; i++) {
                    this.#eventDispatcher.addEventListener(events[i], callback, subscriber);
                }
            }
        }
    };
    dispatchTagEvent = (eventVO, timeout = undefined) => {
        eventVO.uuid = this.uuid;
        const timeNow = new Date().getTime();
        eventVO.msFromStart = timeNow - window.SmartTag.startTime;
        eventVO.timeFromStart = this.#utils.Date.secondsToHHMMSSMS(eventVO.msFromStart / 1000);
        const execute = () => {
            if (this.#utils.Type.isFunction(this?.params?.TAG.callback)) {
                this.params.TAG.callback(eventVO);
            }
            this.#eventDispatcher.dispatchEvent(eventVO);
            if (this.#utils.Type.isDefined(this?.view?.skin) && this.#utils.Type.isFunction(this?.view?.skin?.onTagEvent)) {
                this.view.skin.onTagEvent(eventVO);
            }
        };
        if (isNaN(timeout)) {
            execute();
        }
        else {
            setTimeout(execute, timeout);
        }
    };
    get model() {
        return this.#model;
    }
    get view() {
        return this.#view;
    }
    get controller() {
        return this.#controller;
    }
    get snapshot() {
        if (this.#utils.Type.isEmpty(this.#snapshot)) {
            this.#snapshot = new TagSnapshot({ tag: this });
        }
        return this.#snapshot;
    }
    get API() {
        return this.#api;
    }
    get errorManager() {
        return this.#errorManager;
    }
    #processErrorCodeDebug = () => {
        this.#logger.log('processErrorCodeDebug:', this.#runtimeVO.debug.debugError);
        this.#initTagConfig().then((data) => {
            const errorCode = parseInt(this.#runtimeVO.debug.debugError);
            if (this.#utils.Type.isNumber(errorCode)) {
                switch (errorCode < 1000) {
                    case true:
                        this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_FATAL_ERROR, this, {
                            fatalError: {
                                code: this.#runtimeVO.debug.debugError,
                                msg: {
                                    original: undefined,
                                    custom: this.#errorManager.createErrorMessageByCode(this.#runtimeVO.debug.debugError)
                                },
                                error: data
                            }
                        }));
                        break;
                    case false:
                        this.#eventDispatcher.dispatchEvent(this.getTagEventVO(TagEvent.ON_PLAYER_EVENT, this, {
                            paramsVO: this.params,
                            playerAdapterEvent: {
                                type: PlayerAdapterEvent.ERROR,
                                timeStamp: Date.now(),
                                detail: {
                                    error: {
                                        code: (this.#runtimeVO.debug.debugError),
                                        message: this.#errorManager.createErrorMessageByCode(this.#runtimeVO.debug.debugError),
                                        fatal: true,
                                        category: undefined,
                                        cause: 'TEST'
                                    }
                                },
                                target: undefined
                            }
                        }, this, this.name));
                        break;
                }
            }
            else {
                this.#logger.log(TagLoggerString.INITIALIZE, 'debugError value is invalid:', this.#runtimeVO.debug.debugError);
            }
        });
    };
    #initTagConfig = () => {
        return new Promise((resolve, reject) => {
            this.#model.loadConfigs(ConfigType.TAG, (config) => {
                this.#logger.log('initTagConfig', 'onTagConfigLoadSuccess:', config);
                resolve({ msg: 'Tag configs loaded.' });
            }, (error) => {
                this.#logger.log('onTagConfigLoadFailure:', error);
            }, DefaultTagConfig);
        });
    };
    #initialize = () => {
        this.#logger.log('initialize');
        this.#api = new API({ tag: this });
        this.#view = new TagView({ tag: this });
        this.#model = new TagModel({ tag: this });
        this.#errorManager = new ErrorManager({ tag: this });
        this?.performanceMetricsManager?.setTag(this);
        if (!this.#utils.Type.isEmpty(this.#runtimeVO.debug.debugError) &&
            this.#utils.Type.isNumber(parseInt(this.#runtimeVO.debug.debugError))) {
            this.#processErrorCodeDebug();
            return;
        }
        switch (this?.params?.TAG?.platformSupportCheck) {
            case true:
                platformSupported(this.#runtimeVO.readyCallback)
                    .then((data) => {
                    this.#logger.log(TagLoggerString.INITIALIZE, 'Platform is supported!', data);
                    this.#loadConfigs();
                })
                    .catch((error) => {
                    this.#logger.warn(TagLoggerString.INITIALIZE, 'Platform is not supported!', error);
                    this.#initTagConfig().then((_data) => {
                        this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_FATAL_ERROR, this, {
                            fatalError: {
                                code: TagErrorCode.UNSUPPORTED_BROWSER,
                                msg: {
                                    original: undefined,
                                    custom: this.#errorManager.createErrorMessageByCode(TagErrorCode.UNSUPPORTED_BROWSER)
                                },
                                error: error
                            }
                        }));
                    });
                });
                break;
            case false:
                this.#logger.log(TagLoggerString.INITIALIZE, 'Platform support is disabled. Proceed regardless.');
                this.#loadConfigs();
                break;
        }
    };
    #loadConfigs = () => {
        this.#model.loadConfigs(ConfigType.TAG, this.#onTagConfigLoadSuccess, this.#onTagConfigLoadFailure, DefaultTagConfig);
    };
    #onTagConfigLoadSuccess = (config) => {
        this.#logger.log('onTagConfigLoadSuccess:', config);
        this.#controller = new TagController({
            tag: this,
            onReadyResolve: this.#onReadyResolve,
            onReadyReject: this.#onFailureReject
        });
    };
    #onTagConfigLoadFailure = (error) => {
        this.#logger.log('onTagConfigLoadFailure:', error);
    };
}
